import { BaseComponent } from './base-component.js'
import { customElement, property, state } from 'lit/decorators.js'
import {
  CSSResultGroup,
  TemplateResult,
  html,
  css
} from 'lit'

import { dateIsInThePast, datesAreEqual, formatAsYearMonthDay, getUTCDate } from './utils/date-helpers.js'
import DatePickerDateComponent from './date-picker-date-component.js'
import { animate } from '@lit-labs/motion'
import { animateHeightContainer } from './motion/animations.js'

export interface DatePickerWeek {
  days: DatePickerDay[]
}

export interface DatePickerDay {
  date: Date
  formattedDate: string
  isPastMonth?: boolean // date is the previous past month
  isFutureMonth?: boolean // date is the next future month
  isCurrentMonth?: boolean // date is within this month
  isVisible?: boolean
  isSelectable?: boolean
  isPreselected?: boolean
  isSelected?: boolean
  availabilityDate?: unknown
  samePriceLabel?: string
  showTooltipOnDates?: boolean
  tooltipLabel?: string
}

export interface AvailabilityDate {
  date: string
  availabilityStatus: string
  price: string
  priceRange: [
    {
      format_options: string[]
      amount: string
      currency: string
    }
  ]
  activityIds: string[]
  numStartTimes: number
  idempotencyKey?: string
}

export enum AvailabilityDateStatus {
  AVAILABLE = 'available',
  SOLD_OUT = 'sold_out'
}

export enum DatePickerMode {
  DEFAULT = 'default',
  RESCHEDULE = 'reschedule'
}

/**
 * slots:
 *  - previous-month-icon: icon to navigate to the previous month
 *  - next-month-icon: icon to navigate to the next month
 */
@customElement('il-date-picker')
export default class DatePickerComponent extends BaseComponent {
  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-date-picker-container.il-date-picker-container--is-loading {
        position: relative;
      }

      .il-date-picker-container.il-date-picker-container--is-loading .il-date-picker__dates__day .date-component {
        pointer-events: none;
      }

      .il-date-picker__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .il-date-picker__header__month {
        cursor: pointer;
      }

      .il-date-picker__header__month il-button {
        display: flex;
        justify-content: center;
        width: 45px;
      }

      .il-date-picker__header__month il-button::part(button) {
        width: 100%;
      }

      .il-date-picker__header__month.il-date-picker__header__month--disabled {
        opacity: .3;
        pointer-events: none;
      }

      .il-date-picker__header__current-month-label {
        color: var(--il-color-neutrals-350);
      }

      .il-date-picker__dates__header {
        display: flex;

        margin-bottom: .375rem;
        padding: .5rem 0;
      }

      .il-date-picker__dates__header__date {
        width: calc(100% / 7);
        display: flex;
        align-items: center;
        justify-content: center;

        color: var(--il-color-neutrals-300)
      }

      .il-date-picker__dates__week {
        display: flex;
        gap: 6px;
        margin-bottom: 6px;
        flex-grow: 0;
        flex-shrink: 0;

        &:last-of-type {
          margin-bottom: 0;
        }
      }

      .il-date-picker__dates__day {
        display: flex;
        align-items: center;
        justify-content: center;
        width: calc(100% / 7);
        max-width: calc(100% / 7);
        flex-grow: 0;
        overflow: hidden;
      }

      .il-date-picker__dates__day .date-component {
        width: 100%;
      }

      .il-date-picker .animated-container {
        overflow: hidden;
      }

      .il-date-picker__actions {
        padding-top: 1rem;
      }

      .il-date-picker__divisor {
        /* Use height so the animation can calculate correctly the element size. margin does not work properly */
        height: 1rem;

        border-top: 1px solid var(--il-color-neutrals-100);
      }
    `
  ]

  @state() preselectedDateObject?: Date

  @property({ attribute: 'mode', type: String })
  mode: DatePickerMode = DatePickerMode.DEFAULT

  @property({ attribute: 'center-date', type: String })
  centerDate: Date = new Date()

  @property({ attribute: 'preselected-date', type: String })
  preselectedDate?: Date | string = undefined

  @property({ attribute: 'selected-date', type: String })
  selectedDate?: Date | string = undefined

  @property({ type: Array })
  availabilityDates: AvailabilityDate[] = []

  // controls display of dates from other months that are not the current month
  // ex: if the current month is Feb, and this is set to true, the last week of Jan and first week of March can be displayed
  @property({ attribute: 'can-display-other-months-dates', type: Boolean })
  canDisplayOtherMonthsDates: boolean = false

  // enables selection of past dates (taking new Date() as today)
  @property({ attribute: 'can-select-past-dates', type: Boolean })
  canSelectPastDates: boolean = false

  @property({ attribute: 'locale-override', type: String })
  localeOverride: string = 'default'

  @property({ attribute: 'start-week-on-monday', type: Boolean })
  startWeekOnMonday: boolean = false

  @property({ attribute: 'price-text-label', type: String })
  priceTextLabel: string = '[price]'

  @property({ attribute: 'same-price-label', type: String })
  samePriceLabel: string = '[price]'

  @property({ attribute: 'tooltip-label', type: String })
  tooltipLabel: string = '[price]'

  @property({ attribute: 'show-tooltip-on-dates', type: Boolean })
  showTooltipOnDates: boolean = false

  @property({ attribute: 'sold-out-tooltip-label', type: String })
  soldOutTooltopLabel: string = '[price]'

  @property({ type: Object })
  onMonthChange?: (date: Date) => void

  @property({ type: Object })
  onDateSelect?: (date: DatePickerDay) => void

  @property({ type: Object })
  onDateClear?: (dateToClear?: DatePickerDay) => void

  @property({ type: Boolean })
  tooltipDisableContentHovering?: boolean

  @property({ type: Boolean, attribute: 'hide-prices' })
  hidePrices: boolean = false

  // on DOM insertion:
  override connectedCallback (): void {
    super.connectedCallback()

    if (this.preselectedDate) {
      this.preselectedDateObject = getUTCDate(new Date(this.preselectedDate))
      this.centerDate = getUTCDate(new Date(this.preselectedDate))
    }

    if (this.selectedDate) {
      this.centerDate = getUTCDate(new Date(this.selectedDate))
    }
  }

  get selectedDateObject (): Date | undefined {
    if (!this.selectedDate) {
      return undefined
    }

    return getUTCDate(new Date(this.selectedDate))
  }

  get currentMonth (): Date {
    const centerDate = this.centerDate
    return new Date(centerDate.getFullYear(), centerDate.getMonth(), 1)
  }

  get currentMonthLabel (): string {
    const currentMonthName = this.currentMonth.toLocaleString(this.localeOverride, { month: 'long' })
    const currentYearNumber = this.currentMonth.getFullYear()

    return `${currentMonthName} ${currentYearNumber}`
  }

  get canGoToPreviousMonth (): boolean {
    if (this.canSelectPastDates) {
      return true
    }

    const today = getUTCDate()

    const previousMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1)
    const presentMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    return !dateIsInThePast(previousMonth, presentMonth)
  }

  // returns short weekday names according to given locale
  get weekDays (): string[] {
    const startWeekOnMondayOffset = this.startWeekOnMonday ? 1 : 0
    const weekDays = []
    const today = new Date()
    const startingWeekDay = today.getDate() - today.getDay() + startWeekOnMondayOffset // add 1 here to start the week on Mon
    const startingWeekDate = new Date(today.setDate(startingWeekDay))
    const days = 7

    for (let i = 0; i < days; i++) {
      weekDays.push(startingWeekDate.toLocaleString(this.localeOverride, { weekday: 'short' }))
      startingWeekDate.setUTCDate(startingWeekDate.getDate() + 1)
    }

    return weekDays
  }

  /**
   * grabs this.currentMonth to build a list of DatePickerWeek[] with DatePickerDay[] nested
   * to be rendered as calendar template
   *
   * this is updated each time this.currentMonth changes
   */
  get centerDateWeeks (): DatePickerWeek[] {
    const weeks: DatePickerWeek[] = []
    const startWeekOnMondayOffset = this.startWeekOnMonday ? 1 : 0

    // Use local date methods consistently
    const currentMonth = this.currentMonth
    const currentMonthFirstDay = currentMonth.getDay()

    // Create dates using local methods consistently
    const currentMonthLastDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)
    const currentMonthLastDay = currentMonthLastDate.getDay()

    const currentMonthFirstDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1 + startWeekOnMondayOffset)

    const previousMonthLastDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0)
    const previousMonthLastDateDay = previousMonthLastDate.getDay()

    const currentMonthDays = currentMonthLastDate.getDate()
    const previousMonthDays = previousMonthLastDate.getDate()

    // Rest of the calculation remains the same
    const previousMonthDaysToDisplay = previousMonthLastDateDay === 6 + startWeekOnMondayOffset ? 0 : previousMonthLastDateDay + 1
    const nextMonthDaysToDisplay = currentMonthLastDay === 6 + startWeekOnMondayOffset ? 0 : 6 - currentMonthLastDay
    const totalDaysToDisplay = previousMonthDaysToDisplay + currentMonthDays + nextMonthDaysToDisplay - startWeekOnMondayOffset
    const totalWeeksToDisplay = Math.ceil(totalDaysToDisplay / 7)

    // Create weeks and days
    for (let i = 0; i < totalWeeksToDisplay; i++) {
      const week: DatePickerWeek = {
        days: []
      }

      for (let j = 0; j < 7; j++) {
        let day: DatePickerDay

        if (i === 0 && j < previousMonthDaysToDisplay) {
          // past month
          day = this.createDatePickerDate(
            new Date(
              previousMonthLastDate.getFullYear(),
              previousMonthLastDate.getMonth(),
              previousMonthDays - (previousMonthDaysToDisplay - j) + 1
            )
          )

          this.processPastMonthDate(day)
        } else if (i === totalWeeksToDisplay - 1 && j > 6 - nextMonthDaysToDisplay) {
          // next month
          day = this.createDatePickerDate(
            new Date(
              currentMonthLastDate.getFullYear(),
              currentMonthLastDate.getMonth() + 1,
              j - (6 - nextMonthDaysToDisplay)
            )
          )

          this.processFutureMonthDates(day)
        } else {
          // current month
          day = this.createDatePickerDate(
            new Date(
              currentMonthFirstDate.getFullYear(),
              currentMonthFirstDate.getMonth(),
              (i * 7) + j - currentMonthFirstDay + 1
            )
          )

          this.processPresentMonthDates(day)
        }

        week.days.push(day)
      }

      weeks.push(week)
    }

    return weeks
  }

  /**
   * provides a public API to select date web-components, exposing date components public API
   * to the outside world, can also be used internally
   * @param date
   */
  public findDateWebComponentDOM (date: Date): Element | null {
    if (!date) {
      console.assert(date, 'DatePicker assert error: date is required to find date web component DOM element')
      return null
    }

    const dateToFind = getUTCDate(date)
    const dateStringToFind = formatAsYearMonthDay(dateToFind)
    return this.shadowRoot?.querySelector(`[date-container="${dateStringToFind}"]`) ?? null
  }

  /**
   * provides a public API to change month (to next)
   */
  public goToNextMonth (): void {
    this.centerDate = new Date(
      this.centerDate.getFullYear(),
      this.centerDate.getMonth() + 1, // moves +1 for month
      1
    )

    this.onMonthChange?.(this.centerDate)
  }

  /**
   * provides a public API to change month (to previous)
   */
  public goToPreviousMonth (): void {
    if (!this.canGoToPreviousMonth) {
      return
    }

    this.centerDate = new Date(
      this.centerDate.getFullYear(),
      this.centerDate.getMonth() - 1, // moves -1 for month
      1
    )

    this.onMonthChange?.(this.centerDate)
  }

  protected onDateClick (selectedDate: DatePickerDay): void {
    if (!selectedDate.isSelectable) {
      return
    }

    this.onDateSelect?.(selectedDate)
  }

  protected onClearDate (): void {
    const selectedDate = this.selectedDateObject
    let dateToClear = undefined

    if (selectedDate) {
      const dateElement = this.findDateWebComponentDOM(selectedDate)?.querySelector('il-date-picker-date') as DatePickerDateComponent
      dateToClear = dateElement?.calendarDate ?? undefined
    }

    this.onDateClear?.(dateToClear)
  }

  override render (): TemplateResult {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-date-picker-container
          ${this.isLoading ? 'il-date-picker-container--is-loading' : ''}
        "
        part="date-picker"
        data-test-iliad-date-picker
        data-test-iliad-date-picker-mode="${this.mode}"
      >
        <div class="il-date-picker">
          <div class="il-date-picker__header">
            <div
              class="
                il-date-picker__header__month
                ${!this.canGoToPreviousMonth ? 'il-date-picker__header__month--disabled' : ''}
              "
              data-test-iliad-date-picker-previous-month
              data-test-iliad-date-picker-mode="${this.mode}"
              @click="${() => this.goToPreviousMonth()}"
            >
              <il-button variant="ghost" small icon-only>
                <slot name="previous-month-icon">
                  <
                </slot>
              </il-button>
            </div>

            <div
              class="il-date-picker__header__current-month-label il-font-paragraph-xbold-16"
              data-test-iliad-date-picker-current-month-label
            >
              ${this.currentMonthLabel}
            </div>

            <div
              class="il-date-picker__header__month"
              data-test-iliad-date-picker-next-month
              @click="${() => this.goToNextMonth()}"
            >
              <il-button variant="ghost" small icon-only>
                <slot name="next-month-icon">
                  >
                </slot>
              </il-button>
            </div>
          </div>

          <div class="il-date-picker__dates">
            <div class="il-date-picker__dates__header">
              ${this.weekDays.map((weekDay, index) => {
                return html`
                  <div
                    class="il-date-picker__dates__header__date il-font-paragraph-normal-16"
                    data-test-iliad-date-picker-week-header="${index}"
                  >
                    ${weekDay}
                  </div>
                `
              })}
            </div>

            <div class="il-date-picker__dates__date-list">
              ${this.centerDateWeeks.map((datePickerWeek: DatePickerWeek) =>
                html`
                  <div class="il-date-picker__dates__week">
                    ${datePickerWeek.days.map((datePickerDay: DatePickerDay) =>
                      this.renderDate(datePickerDay)
                    )}
                  </div>
                `
              )}
            </div>
          </div>

          <div
            class="animated-container"
            ${animate(animateHeightContainer({ fast: this.fastAnimation, animationCurve: this.animationCurve }))}
          >
            ${this.renderActions()}
          </div>
          <slot name="alerts"></slot>
        </div>

        ${this.renderLoadingState()}
      </div>
    `
  }

  /**
   * renders dates
   * have in mind that this method always return some HTML so calendar spaces are always filled by
   * something instead of empty spaces, so layout keeps consistent with what a calendar should look
   * @param datePickerDay: DatePickerDay
   * @private
   */
  private renderDate (datePickerDay: DatePickerDay): TemplateResult {
    const isOutOfMonth = datePickerDay.isPastMonth || datePickerDay.isFutureMonth
    const isVisible = datePickerDay.isVisible

    if (!isOutOfMonth || (isOutOfMonth && isVisible)) {
      return html`
        <div
          class="
            il-date-picker__dates__day
            ${datePickerDay.isFutureMonth || datePickerDay.isPastMonth ? 'il-date-picker__dates__day--out-of-month' : ''}
          "
          date-container="${datePickerDay.formattedDate}"
          data-test-iliad-date-picker-date-container="${datePickerDay.formattedDate}"
          part="date-picker-date-container"
        >
          ${this.renderDateComponent(datePickerDay)}
        </div>
      `
    }

    return html`
      <div class="il-date-picker__dates__day">

      </div>
    `
  }

  private renderDateComponent (datePickerDay: DatePickerDay): TemplateResult {
    if (this.mode === DatePickerMode.RESCHEDULE) {
      return html`
        <il-date-picker-date-reschedule
          class="date-component"
          ?is-loading=${this.isLoading}
          price-text-override="${this.priceTextLabel}"
          same-price-label="${this.samePriceLabel}"
          tooltip-text-override="${this.tooltipLabel}"
          ?should-show-tooltip="${this.showTooltipOnDates}"
          sold-out-tooltip-label="${this.soldOutTooltopLabel}"
          ?tooltip-disable-content-hovering="${this.tooltipDisableContentHovering}"
          .calendarDate="${datePickerDay}"
          .enablePastDates="${this.canSelectPastDates}"
          ?hide-prices="${this.hidePrices}"
          .onClick="${this.onDateClick.bind(this)}"
        ></il-date-picker-date-reschedule>
      `
    }

    return html`
      <il-date-picker-date
        class="date-component"
        ?is-loading=${this.isLoading}
        price-text-override="${this.priceTextLabel}"
        same-price-label="${this.samePriceLabel}"
        tooltip-text-override="${this.tooltipLabel}"
        ?should-show-tooltip="${this.showTooltipOnDates}"
        sold-out-tooltip-label="${this.soldOutTooltopLabel}"
        ?tooltip-disable-content-hovering="${this.tooltipDisableContentHovering}"
        .calendarDate="${datePickerDay}"
        .enablePastDates="${this.canSelectPastDates}"
        ?hide-prices="${this.hidePrices}"
        .onClick="${this.onDateClick.bind(this)}"
      ></il-date-picker-date>
    `
  }

  /**
   * creates a base date picker date object that will be used to host availability and
   * calendar visibility/selectability rules
   * @param date: Date
   */
  private createDatePickerDate (date: Date): DatePickerDay {
    const availabilityDate = this.availabilityDates.find(
      (availabilityDate) => {
        return datesAreEqual(getUTCDate(new Date(availabilityDate.date)), date)
      }
    )

    return {
      date,
      formattedDate: formatAsYearMonthDay(date),
      isPreselected: this.preselectedDateObject ? datesAreEqual(date, this.preselectedDateObject) : false,
      isSelected: this.selectedDateObject ? datesAreEqual(date, this.selectedDateObject) : false,
      availabilityDate
    }
  }

  protected renderActions (): TemplateResult {
    if (this.selectedDateObject && !(this.mode === DatePickerMode.RESCHEDULE)) {
      return html`
        <div
          class="il-date-picker__actions" part="date-picker-actions"
        >
          <div class="il-date-picker__divisor"></div>
          <il-button
            small
            variant="tertiary"
            @click="${() => this.onClearDate()}"
            data-test-date-picker-action="clear-date"
          >
            Clear Date
          </il-button>
        </div>
      `
    }

    return html``
  }

  private processPastMonthDate (date: DatePickerDay): void {
    date.isPastMonth = true
    date.isVisible = this.canDisplayOtherMonthsDates
    date.isSelectable = this.canSelectPastDates
  }

  private processFutureMonthDates (date: DatePickerDay): void {
    date.isFutureMonth = true
    date.isVisible = this.canDisplayOtherMonthsDates
    date.isSelectable = true
  }

  private processPresentMonthDates (date: DatePickerDay): void {
    date.isCurrentMonth = true
    date.isVisible = true
    date.isSelectable = true
  }
}
